import React, { useState, useEffect } from 'react';
import { App, Card, Table, Button, Form, Input, Modal, Space, Descriptions, Spin } from 'antd';
import { PlusOutlined, MailOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import { formatDate } from '../utils/format';

interface AdminUser {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  created_at: string;
}

interface Store {
  id: string;
  name: string;
  default_currency_code: string;
  default_sales_channel_id?: string;
  default_region_id?: string;
  created_at: string;
}

const Settings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [store, setStore] = useState<Store | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteForm] = Form.useForm();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, storeRes] = await Promise.allSettled([
        apiClient.get('/admin/users'),
        apiClient.get('/admin/stores'),
      ]);
      if (usersRes.status === 'fulfilled') {
        setUsers(usersRes.value.data.users || []);
      }
      if (storeRes.status === 'fulfilled') {
        const stores = storeRes.value.data.stores || [];
        setStore(stores[0] || null);
      }
    } catch (error: any) {
      message.error(t('settings.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (values: { email: string }) => {
    try {
      setInviteLoading(true);
      await apiClient.post('/admin/invites', { email: values.email });
      message.success(t('settings.inviteSuccess'));
      setInviteModalVisible(false);
      inviteForm.resetFields();
      loadData();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('settings.inviteError'));
    } finally {
      setInviteLoading(false);
    }
  };

  const userColumns = [
    {
      title: t('common.email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('customers.firstName'),
      dataIndex: 'first_name',
      key: 'first_name',
      render: (name: string) => name || '-',
    },
    {
      title: t('customers.lastName'),
      dataIndex: 'last_name',
      key: 'last_name',
      render: (name: string) => name || '-',
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => formatDate(date, i18n.language),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ fontSize: 24, fontWeight: 600, color: '#334E3B', marginBottom: 24 }}>
        {t('settings.title')}
      </h1>

      {store && (
        <Card title={t('settings.store')} style={{ marginBottom: 24 }}>
          <Descriptions column={{ xs: 1, sm: 2, md: 3 }}>
            <Descriptions.Item label={t('common.name')}>{store.name}</Descriptions.Item>
            <Descriptions.Item label={t('settings.currency')}>{store.default_currency_code?.toUpperCase() || 'EUR'}</Descriptions.Item>
            <Descriptions.Item label={t('common.createdAt')}>
              {formatDate(store.created_at, i18n.language)}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      <Card
        title={t('settings.adminUsers')}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setInviteModalVisible(true)}
          >
            {t('settings.inviteUser')}
          </Button>
        }
      >
        <Table
          columns={userColumns}
          dataSource={users}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: t('common.noData') }}
        />
      </Card>

      <Modal
        title={t('settings.inviteUser')}
        open={inviteModalVisible}
        onCancel={() => setInviteModalVisible(false)}
        footer={null}
      >
        <Form
          form={inviteForm}
          layout="vertical"
          onFinish={handleInvite}
        >
          <Form.Item
            name="email"
            label={t('common.email')}
            rules={[
              { required: true, message: t('auth.emailRequired') },
              { type: 'email', message: t('auth.emailInvalid') },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder={t('auth.enterEmail')} />
          </Form.Item>
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setInviteModalVisible(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="primary" htmlType="submit" loading={inviteLoading}>
                {t('settings.sendInvite')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Settings;
